/**
 * tableroLogica.js — Lógica del tablero de juego (casilleros, comodines, efectos).
 *
 * Exports:
 *   calcularCasilleros(params)   → número de casilleros a mover
 *   avanzarEnTablero(...)        → mueve un participante y aplica efectos
 *   fetchComodines(torneoId, userId) → comodines disponibles del usuario
 *   usarComodin(comodinId, ...)  → marca un comodín como usado
 */
import { supabase } from '../../supabaseClient';

// ── Constantes ──────────────────────────────────────────────────────────────

const TOTAL_CASILLEROS = 26;

/** Pool de tipos de comodín para sorteos aleatorios — deben coincidir exactamente con comodin_tipos.id */
const TIPOS_COMODIN = [
  'dinero_extra',
  'dinero_extra_plus',
  'ver_rival',
  'cambiar_restriccion',
  'anular_restriccion',
  'sabotaje',
  'sabotaje_plus',
  'restriccion_extra_lider',
];

/** Devuelve un tipo de comodín al azar del pool */
const comodinAleatorio = () =>
  TIPOS_COMODIN[Math.floor(Math.random() * TIPOS_COMODIN.length)];


// ── Cálculo de movimiento ────────────────────────────────────────────────────

/**
 * Calcula cuántos casilleros debe avanzar un jugador en una fecha.
 * @param {number} posicion           - Posición en el ranking (1 = primero)
 * @param {number} cantidadRestricciones - Cant. de restricciones cumplidas
 * @param {boolean} restriccionConGol  - Si alguna restricción incluyó un gol
 */
export const calcularCasilleros = ({ posicion, cantidadRestricciones, restriccionConGol }) => {
  const bonusPosicion = posicion === 1 ? 2 : posicion <= 3 ? 1 : 0;
  const bonusGol      = restriccionConGol ? 1 : 0;
  return cantidadRestricciones + bonusPosicion + bonusGol;
};


// ── Movimiento en el tablero ─────────────────────────────────────────────────

/**
 * Mueve un participante en el tablero y aplica el efecto del casillero donde cae.
 * @returns {object|null} Resumen del movimiento o null si el participante no existe
 */
export const avanzarEnTablero = async (torneoId, userId, casillerosMover) => {
  const [{ data: participante }, { data: configs }] = await Promise.all([
    supabase
      .from('torneo_participantes')
      .select('casillero, vueltas')
      .eq('torneo_id', torneoId)
      .eq('user_id', userId)
      .single(),
    supabase
      .from('casillero_config')
      .select('*')
      .order('casillero'),
  ]);

  if (!participante) return null;

  const casilleroActual  = participante.casillero || 0;
  const vueltasActuales  = participante.vueltas || 0;
  const nuevoPosRaw      = casilleroActual + casillerosMover;
  const vueltasNuevas    = Math.floor(nuevoPosRaw / TOTAL_CASILLEROS);
  let   casilleroFinal   = nuevoPosRaw % TOTAL_CASILLEROS;

  // Mapa casillero → configuración
  // Deduplicar — tomar primer registro por casillero
  const configMap = {};
  (configs || []).forEach(c => { if (!configMap[c.casillero]) configMap[c.casillero] = c; });

  const comodinesGanados = [];
  const efectos          = [];

  // 1. Comodín por completar una vuelta completa
  if (vueltasNuevas > 0) {
    for (let v = 0; v < vueltasNuevas; v++) {
      comodinesGanados.push({ tipo: comodinAleatorio(), motivo: 'vuelta_completa' });
    }
    efectos.push({ tipo: 'vuelta_completa', vueltas: vueltasNuevas });
  }

  // 2. Efecto del casillero de destino
  const config = configMap[casilleroFinal];
  if (config) {
    switch (config.tipo) {

      case 'comodin_aleatorio':
        comodinesGanados.push({ tipo: comodinAleatorio(), motivo: `casillero_${casilleroFinal}` });
        efectos.push({ tipo: 'comodin_aleatorio', casillero: casilleroFinal, emoji: config.emoji });
        break;

      case 'comodin_fijo':
        comodinesGanados.push({ tipo: config.comodin_tipo_id, motivo: `casillero_${casilleroFinal}` });
        efectos.push({ tipo: 'comodin_fijo', comodin: config.comodin_tipo_id, casillero: casilleroFinal, emoji: config.emoji });
        break;

      case 'trampa_retroceso': {
        const retroceso = config.casilleros_efecto || 3;
        casilleroFinal = ((casilleroFinal - retroceso) % TOTAL_CASILLEROS + TOTAL_CASILLEROS) % TOTAL_CASILLEROS;
        efectos.push({ tipo: 'trampa_retroceso', retroceso, casilleroFinal, emoji: config.emoji });
        // Aplicar efecto del casillero donde aterrizás (no encadena otro retroceso para evitar loop)
        const configDestRet = configMap[casilleroFinal];
        if (configDestRet && configDestRet.tipo !== 'trampa_retroceso') {
          if (configDestRet.tipo === 'comodin_aleatorio') {
            comodinesGanados.push({ tipo: comodinAleatorio(), motivo: `casillero_${casilleroFinal}` });
            efectos.push({ tipo: 'comodin_aleatorio', casillero: casilleroFinal, emoji: configDestRet.emoji });
          } else if (configDestRet.tipo === 'comodin_fijo') {
            comodinesGanados.push({ tipo: configDestRet.comodin_tipo_id, motivo: `casillero_${casilleroFinal}` });
            efectos.push({ tipo: 'comodin_fijo', comodin: configDestRet.comodin_tipo_id, casillero: casilleroFinal, emoji: configDestRet.emoji });
          } else if (configDestRet.tipo === 'trampa_combo') {
            // Combo encadenado → también va a la PRÓXIMA jornada
            const retroDest = configDestRet.casilleros_efecto || 2;
            const montoDest = configDestRet.monto_efecto || 1000000;
            casilleroFinal = ((casilleroFinal - retroDest) % TOTAL_CASILLEROS + TOTAL_CASILLEROS) % TOTAL_CASILLEROS;
            const { data: ventanaEnc } = await supabase.from('ventana_transferencias')
              .select('numero_fecha').eq('activa', true)
              .order('numero_fecha', { ascending: false }).limit(1).maybeSingle();
            const jornadaSigEnc = (ventanaEnc?.numero_fecha ?? 1) + 1;
            const { data: draftEnc } = await supabase.from('drafts')
              .select('efectos_comodines').eq('user_id', userId).eq('jornada', jornadaSigEnc).maybeSingle();
            const efectosEnc = draftEnc?.efectos_comodines || [];
            efectosEnc.push({ tipo: 'trampa_combo', monto: -montoDest, descripcion: configDestRet.descripcion });
            await supabase.from('drafts').upsert({ user_id: userId, jornada: jornadaSigEnc, efectos_comodines: efectosEnc }, { onConflict: 'user_id,jornada' });
            efectos.push({ tipo: 'trampa_combo', retroceso: retroDest, monto: montoDest, casilleroFinal, emoji: configDestRet.emoji, descripcion: configDestRet.descripcion, jornada: jornadaSigEnc });
          } else if (configDestRet.tipo === 'trampa_pierde_turno') {
            await supabase.from('torneo_participantes').update({ pierde_turno: true }).eq('torneo_id', torneoId).eq('user_id', userId);
            efectos.push({ tipo: 'trampa_pierde_turno', casillero: casilleroFinal, emoji: configDestRet.emoji });
          } else if (configDestRet.tipo === 'bonus_avance') {
            const bonusDest = configDestRet.casilleros_efecto || 2;
            casilleroFinal = (casilleroFinal + bonusDest) % TOTAL_CASILLEROS;
            efectos.push({ tipo: 'bonus_avance', bonus: bonusDest, casilleroFinal, emoji: configDestRet.emoji });
          }
        }
        break;
      }

      case 'trampa_combo': {
        // Retrocede casilleros Y descuenta presupuesto en la PRÓXIMA jornada
        const retroceso = config.casilleros_efecto || 2;
        const monto     = config.monto_efecto || 1000000;
        casilleroFinal  = ((casilleroFinal - retroceso) % TOTAL_CASILLEROS + TOTAL_CASILLEROS) % TOTAL_CASILLEROS;

        // Obtener jornada activa y aplicar a la SIGUIENTE
        const { data: ventana } = await supabase.from('ventana_transferencias')
          .select('numero_fecha').eq('activa', true)
          .order('numero_fecha', { ascending: false }).limit(1).maybeSingle();
        const jornadaSiguiente = (ventana?.numero_fecha ?? 1) + 1;

        const { data: draftSig } = await supabase.from('drafts')
          .select('efectos_comodines')
          .eq('user_id', userId)
          .eq('jornada', jornadaSiguiente)
          .maybeSingle();
        const efectosSig = draftSig?.efectos_comodines || [];
        efectosSig.push({ tipo: 'trampa_combo', monto: -monto, descripcion: config.descripcion });
        await supabase.from('drafts').upsert({
          user_id: userId, jornada: jornadaSiguiente, efectos_comodines: efectosSig
        }, { onConflict: 'user_id,jornada' });

        efectos.push({ tipo: 'trampa_combo', retroceso, monto, casilleroFinal, emoji: config.emoji, descripcion: config.descripcion, jornada: jornadaSiguiente });
        break;
      }

      case 'bonus_avance': {
        const bonus = config.casilleros_efecto || 2;
        casilleroFinal = (casilleroFinal + bonus) % TOTAL_CASILLEROS;
        efectos.push({ tipo: 'bonus_avance', bonus, casilleroFinal, emoji: config.emoji });
        // Aplicar también el efecto del casillero donde se aterriza
        const configDestino = configMap[casilleroFinal];
        if (configDestino) {
          if (configDestino.tipo === 'comodin_aleatorio') {
            comodinesGanados.push({ tipo: comodinAleatorio(), motivo: `casillero_${casilleroFinal}` });
            efectos.push({ tipo: 'comodin_aleatorio', casillero: casilleroFinal, emoji: configDestino.emoji });
          } else if (configDestino.tipo === 'comodin_fijo') {
            comodinesGanados.push({ tipo: configDestino.comodin_tipo_id, motivo: `casillero_${casilleroFinal}` });
            efectos.push({ tipo: 'comodin_fijo', comodin: configDestino.comodin_tipo_id, casillero: casilleroFinal, emoji: configDestino.emoji });
          } else if (configDestino.tipo === 'trampa_pierde_turno') {
            await supabase.from('torneo_participantes').update({ pierde_turno: true }).eq('torneo_id', torneoId).eq('user_id', userId);
            efectos.push({ tipo: 'trampa_pierde_turno', casillero: casilleroFinal, emoji: configDestino.emoji });
          }
          // trampa_retroceso y trampa_combo no se encadenan desde bonus_avance
        }
        break;
      }

      case 'trampa_pierde_turno':
        await supabase
          .from('torneo_participantes')
          .update({ pierde_turno: true })
          .eq('torneo_id', torneoId)
          .eq('user_id', userId);
        efectos.push({ tipo: 'trampa_pierde_turno', casillero: casilleroFinal, emoji: config.emoji });
        break;

      default:
        break;
    }
  }

  // Persistir nueva posición
  await supabase
    .from('torneo_participantes')
    .update({ casillero: casilleroFinal, vueltas: vueltasActuales + vueltasNuevas })
    .eq('torneo_id', torneoId)
    .eq('user_id', userId);

  // Guardar comodines ganados — máximo 7 simultáneos
  if (comodinesGanados.length > 0) {
    const MAX_COMODINES = 7;

    // Contar cuántos comodines sin usar tiene el usuario
    const { count: cantActual } = await supabase
      .from('comodines_usuario')
      .select('*', { count: 'exact', head: true })
      .eq('torneo_id', torneoId)
      .eq('user_id', userId)
      .eq('usado', false);

    const espacioDisponible = MAX_COMODINES - (cantActual || 0);

    if (espacioDisponible > 0) {
      // Solo insertar los que entran
      const comodinesAInsertar = comodinesGanados.slice(0, espacioDisponible);
      await supabase.from('comodines_usuario').insert(
        comodinesAInsertar.map(c => ({
          torneo_id: torneoId,
          user_id:   userId,
          tipo_id:   c.tipo,
        }))
      );
    } else {
      // Tiene 7 → reemplazar uno al azar
      const { data: comodinesActuales } = await supabase
        .from('comodines_usuario')
        .select('id')
        .eq('torneo_id', torneoId)
        .eq('user_id', userId)
        .eq('usado', false);

      if (comodinesActuales?.length) {
        const idx = Math.floor(Math.random() * comodinesActuales.length);
        const idReemplazar = comodinesActuales[idx].id;
        // Reemplazar el comodín sorteado con el nuevo
        await supabase.from('comodines_usuario')
          .update({ tipo_id: comodinesGanados[0].tipo })
          .eq('id', idReemplazar);
      }
    }
  }

  return {
    casilleroAnterior:  casilleroActual,
    casilleroFinal,
    vueltasCompletadas: vueltasNuevas,
    totalVueltas:       vueltasActuales + vueltasNuevas,
    comodinesGanados,
    efectos,
  };
};


// ── Comodines ────────────────────────────────────────────────────────────────

/**
 * Trae los comodines disponibles (no usados) de un usuario en un torneo.
 */
export const fetchComodines = async (torneoId, userId) => {
  const { data } = await supabase
    .from('comodines_usuario')
    .select('*, tipo:comodin_tipos(id, nombre, descripcion, emoji, monto)')
    .eq('torneo_id', torneoId)
    .eq('user_id', userId)
    .eq('usado', false)
    .order('fecha_obtenido', { ascending: true });
  return data || [];
};

/**
 * Marca un comodín como usado.
 * @returns {boolean} true si se guardó correctamente
 */
export const usarComodin = async (comodinId, fechaId, objetivoUserId = null) => {
  const { error } = await supabase
    .from('comodines_usuario')
    .update({
      usado:              true,
      fecha_usado:        new Date().toISOString(),
      usado_en_fecha_id:  fechaId,
      objetivo_user_id:   objetivoUserId,
    })
    .eq('id', comodinId);
  return !error;
};
/** Tipos de comodín negativos y positivos (para límite por fecha) */
const COMODINES_NEGATIVOS = new Set(['sabotaje', 'sabotaje_plus', 'sabotaje_plusplus', 'cambiar_restriccion', 'anular_restriccion', 'restriccion_extra_lider']);
const COMODINES_POSITIVOS = new Set(['dinero_extra', 'dinero_extra_plus', 'dinero_extra_plusplus', 'ver_rival']);
const MAX_COMODINES_POR_TIPO_FECHA = 2;

/**
 * Verifica si se puede aplicar un comodín a un objetivo en una fecha.
 * Límite: 2 negativos y 2 positivos recibidos por fecha.
 * Los efectos de casillero (trampa_combo, trampa_retroceso) no tienen límite.
 * @returns {{ puede: boolean, motivo: string|null }}
 */
export const verificarLimiteComodin = async (torneoId, objetivoUserId, fechaId, tipoId) => {
  const esNegativo = COMODINES_NEGATIVOS.has(tipoId);
  const esPositivo = COMODINES_POSITIVOS.has(tipoId);

  if (!esNegativo && !esPositivo) return { puede: true, motivo: null };

  const tiposAContar = esNegativo ? [...COMODINES_NEGATIVOS] : [...COMODINES_POSITIVOS];

  const { count } = await supabase
    .from('comodines_usuario')
    .select('*', { count: 'exact', head: true })
    .eq('torneo_id', torneoId)
    .eq('objetivo_user_id', objetivoUserId)
    .eq('usado_en_fecha_id', fechaId)
    .eq('usado', true)
    .in('tipo_id', tiposAContar);

  if ((count || 0) >= MAX_COMODINES_POR_TIPO_FECHA) {
    return {
      puede: false,
      motivo: esNegativo
        ? `Este jugador ya recibió ${MAX_COMODINES_POR_TIPO_FECHA} comodines en su contra esta fecha.`
        : `Este jugador ya recibió ${MAX_COMODINES_POR_TIPO_FECHA} comodines en su contra esta fecha.`,
    };
  }

  return { puede: true, motivo: null };
};

// Calcula el array de casilleros que se recorren desde posActual hasta posActual+casillerosMover
export const calcularPathCasilleros = (posActual, casillerosMover, total = TOTAL_CASILLEROS) => {
  const path = [];
  for (let i = 1; i <= casillerosMover; i++) {
    path.push((posActual + i) % total);
  }
  return path;
};